import React, { useState, useEffect, useRef } from 'react';
import {
  getAllCountries,
  getIndianStatesAndUTs,
  getCitiesForLocation,
  getResolvedCityValue,
  isIndia,
  isCitySelectionReady,
  STATE_NOT_APPLICABLE,
  CITY_OTHER
} from '../../utils/locationData';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import axios from 'axios';
import API_CONFIG from '../../config/apiConfig';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  MapPin, 
  GraduationCap,
  Save,
  ArrowRight,
  AlertCircle,
  Users,
  FileText
} from 'lucide-react';
import Header from '../common/Header';

const SearchableSelect = ({ id, name, value, onChange, options, placeholder, required, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const filteredOptions = (options || []).filter(opt =>
    opt.toLowerCase().includes(searchText.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    onChange({ target: { name, value: optionValue } });
    setIsOpen(false);
    setSearchText('');
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={id}
        onClick={() => { if (!disabled) setIsOpen(prev => !prev); }}
        disabled={disabled}
        style={{ display: 'flex', alignItems: 'center', minHeight: '42px' }}
        className={[
          'w-full px-3 py-2 text-sm text-left',
          'border rounded-md shadow-sm',
          'transition-colors duration-150',
          'focus:outline-none',
          isOpen
            ? 'border-teal-500 ring-2 ring-teal-500 ring-opacity-30'
            : 'border-gray-300',
          disabled
            ? 'bg-gray-100 opacity-60 cursor-not-allowed'
            : 'bg-white cursor-pointer hover:border-gray-400',
        ].join(' ')}
      >
        <span className={`flex-1 truncate leading-5 ${value ? 'text-gray-900' : 'text-gray-400'}`}>
          {value || placeholder}
        </span>
        <svg
          className={`h-4 w-4 ml-2 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180 text-teal-500' : 'text-gray-400'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
          <div className="p-2 bg-gray-50 border-b border-gray-100">
            <div className="relative">
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <li className="px-4 py-3 text-sm text-gray-400 text-center">No options found</li>
            ) : (
              filteredOptions.map((option) => (
                <li
                  key={option}
                  onClick={() => handleSelect(option)}
                  className={`flex items-center px-4 py-2.5 text-sm cursor-pointer transition-colors duration-100 ${
                    value === option
                      ? 'bg-teal-50 text-teal-800 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {value === option && (
                    <span className="mr-2 h-1.5 w-1.5 rounded-full bg-teal-500 flex-shrink-0 inline-block" />
                  )}
                  {option}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const Stage1Form = () => {
  console.log('🚀 Stage1Form: Component function called');
  console.log('🚀 Stage1Form: Component is rendering at:', new Date().toISOString());
  
  const { user, isAuthenticated } = useAuth();
  console.log('🚀 Stage1Form: useAuth hook called successfully');
  
  const navigate = useNavigate();
  console.log('🚀 Stage1Form: useNavigate hook called successfully');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  console.log('🚀 Stage1Form: All useState hooks initialized');
  
  // Simple test to see if component is working
  console.log('🚀 Stage1Form: About to initialize formData state');
  
  const [formData, setFormData] = useState({
    // Campus & Curriculum
    preferredCampus: 'Bangalore', // Default to Bangalore, not editable
    curriculum: 'CBSE', // Default to CBSE, not editable
    
    // Student Information
    studentFirstName: '',
    studentMiddleName: '',
    studentLastName: '',
    gender: '',
    dateOfBirth: '',
    motherTongue: '',
    otherMotherTongue: '',
    gradeApplying: '',
    session: '',
    nationality: '',
    religion: '',
    otherReligion: '',
    caste: '',
    casteCertificateName: '',

    // Location (Country → State → City)
    address: {
      country: '',
      state: '',
      city: '',
      otherCity: ''
    },
    
    // Father Information
    fatherFirstName: '',
    fatherMiddleName: '',
    fatherLastName: '',
    fatherCountryCode: '',
    fatherPhoneNumber: '',
    fatherEmail: '',
    
    // Mother Information
    motherFirstName: '',
    motherMiddleName: '',
    motherLastName: '',
    motherCountryCode: '',
    motherPhoneNumber: '',
    motherEmail: '',
    
    // Guardian Information
    guardianFirstName: '',
    guardianMiddleName: '',
    guardianLastName: '',
    guardianCountryCode: '',
    guardianPhoneNumber: '',
    guardianEmail: '',
    guardianRelationship: '',
    
    // Family Information
    familyStructure: '',
    maritalStatus: '',
    singleParentMaritalStatus: '',
    
    // Legacy fields for backward compatibility
    firstName: '',
    lastName: '',
    email: '',
    gradeApplied: '',
    
    // NEW: Application Stage 1 Status from Zoho CRM
    applicationStage1Filled: 'No'
  });

  // Prevent form data from being reset to empty values
  const setFormDataSafely = (newData) => {
    console.log('🔒 setFormDataSafely called with:', newData);
    console.log('🔒 Current formData before update:', formData);
    console.log('🔒 Data already loaded flag:', dataLoaded);
    console.log('🔒 New data keys:', newData ? Object.keys(newData) : 'No data');
    console.log('🔒 New data sample values:', {
      preferredCampus: newData?.preferredCampus,
      studentFirstName: newData?.studentFirstName,
      studentLastName: newData?.studentLastName
    });
    
    // Only update if we have actual data to set
    if (newData && (newData.preferredCampus || newData.curriculum || newData.studentFirstName || newData.studentLastName)) {
      console.log('🔒 Updating form data with actual data');
      setFormData(newData);
      setDataLoaded(true);
      console.log('🔒 Data loaded flag set to true');
      
      // Verify the update
      setTimeout(() => {
        console.log('🔒 VERIFICATION: Form data after safe update:', formData);
      }, 100);
    } else {
      console.log('🔒 Skipping form data update - no actual data provided');
    }
  };
  
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dialCode, setDialCode] = useState('');
  const [countryName, setCountryName] = useState('');
  
  // Phone number states for family members
  const [fatherPhone, setFatherPhone] = useState('');
  const [motherPhone, setMotherPhone] = useState('');
  const [guardianPhone, setGuardianPhone] = useState('');

  // Helper function to get button text based on application stage status
  const getApplicationButtonText = () => {
    if (formData.applicationStage1Filled === 'Yes') {
      return 'View/Edit Application';
    } else {
      return 'Start Application';
    }
  };

  // Helper function to format date for HTML date input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Handle different date formats from Zoho CRM
      let date;
      if (typeof dateString === 'string') {
        // Try to parse the date string
        date = new Date(dateString);
      } else if (dateString instanceof Date) {
        date = dateString;
      } else {
        return '';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.log('⚠️ Invalid date from Zoho CRM:', dateString);
        return '';
      }
      
      // Format as YYYY-MM-DD for HTML date input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      const formattedDate = `${year}-${month}-${day}`;
      console.log('📅 Date formatted for input:', { original: dateString, formatted: formattedDate });
      return formattedDate;
    } catch (error) {
      console.error('❌ Error formatting date:', error);
      return '';
    }
  };
  
  // State to track if Zoho data was loaded
  const [zohoDataLoaded, setZohoDataLoaded] = useState(false);
  const [zohoDataSource, setZohoDataSource] = useState(null);

  const campuses = [
    'Bangalore',
    'Indore', 
    'Edify Chikkabanavara'
  ];

  const boards = [
    'CBSE'
  ];

  const sessions = [
    '2025-26',
    '2026-27'
  ];

  const grades = [
    'Class 4',
    'Class 5',
    'Class 6',
    'Class 7',
    'Class 8',
    'Class 9',
    'Class 10',
    'Class 11',
    'Class 12'
  ];

  const genders = ['Male', 'Female'];

  const familyStructures = [
    'Both parents present',
    'Single parent - Father only',
    'Single parent - Mother only',
    'Separated/Divorced parents',
    'Parents not present - Child living with Guardian'
  ];

  const guardianRelationships = [
    'Grandfather',
    'Grandmother',
    'Uncle',
    'Aunt',
    'Legal Guardian',
    'Foster Parent',
    'Other'
  ];

  const maritalStatuses = [
    'Married',
    'Single parent'
  ];

  const singleParentOptions = [
    'Separated',
    'Divorced',
    'Widow/widower',
    'None of the above'
  ];

  const nationalities = [
    'Indian student',
    'NRI Student',
    'Non-Indian student'
  ];

  const motherTongues = [
    'Assamese',
    'Bangla',
    'Bodo',
    'Dogri',
    'Gujarati',
    'Hindi',
    'Kashmiri',
    'Kannada',
    'Konkani',
    'Maithili',
    'Malayalam',
    'Manipuri',
    'Marathi',
    'Nepali',
    'Oriya',
    'Punjabi',
    'Tamil',
    'Telugu',
    'Santali',
    'Sindhi',
    'Urdu',
    'Other'
  ];

  const religions = [
    'Agnostic',
    'Atheist',
    'Buddhism',
    'Christianity',
    'Hinduism',
    'Islam',
    'Jainism',
    'Neo-Paganism',
    'Non-Religious',
    'Primal-Indigenous',
    'Rastafarianism',
    'Secular',
    'Shinto',
    'Sikhism',
    'Spiritism',
    'Tenrikyo',
    'Other'
  ];

  const castes = [
    'General',
    'SC',
    'ST',
    'OBC'
  ];

  const [countries, setCountries] = useState([]);
  const [indianStates, setIndianStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [locationDataLoading, setLocationDataLoading] = useState(true);

  const cityDropdownReady = isCitySelectionReady(
    formData.address?.country,
    formData.address?.state
  );

  useEffect(() => {
    let cancelled = false;

    const loadLocationLists = async () => {
      setLocationDataLoading(true);
      try {
        const [countryList, stateList] = await Promise.all([
          getAllCountries(),
          getIndianStatesAndUTs()
        ]);
        if (!cancelled) {
          setCountries(countryList);
          setIndianStates(stateList);
        }
      } catch (err) {
        console.error('Failed to load location data:', err);
      } finally {
        if (!cancelled) {
          setLocationDataLoading(false);
        }
      }
    };

    loadLocationLists();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCities = async () => {
      if (!cityDropdownReady) {
        setAvailableCities([]);
        return;
      }

      try {
        const cities = await getCitiesForLocation(
          formData.address?.country,
          formData.address?.state
        );
        if (!cancelled) {
          setAvailableCities(cities);
        }
      } catch (err) {
        console.error('Failed to load cities:', err);
        if (!cancelled) {
          setAvailableCities([]);
        }
      }
    };

    loadCities();
    return () => {
      cancelled = true;
    };
  }, [formData.address?.country, formData.address?.state, cityDropdownReady]);

  // Single, clear data loading effect - simplified to prevent infinite loops
  useEffect(() => {
    console.log('🔍 Stage1Form: Component mounted, auth state:', { user, isAuthenticated });
    
    // Reset redirecting state on mount
    setIsRedirecting(false);
    
    // Load data when user is ready
    if (isAuthenticated && user) {
      console.log('🔍 Stage1Form: User ready, loading existing data...');
      // Add a small delay to prevent rapid API calls
      const timer = setTimeout(() => {
        loadExistingData();
      }, 500);
      
      return () => clearTimeout(timer);
    } else {
      console.log('🔍 Stage1Form: User not ready:', { 
        isAuthenticated, 
        hasUser: !!user
      });
    }
  }, [isAuthenticated, user]); // Removed dataLoaded to prevent infinite loops

  // Removed aggressive data loading useEffect to prevent infinite loops

  // Removed monitoring and periodic useEffect hooks to prevent infinite loops

  // Fetch payment status when component loads
  useEffect(() => {
    const loadPaymentStatus = async () => {
      if (isAuthenticated && user) {
        const status = await fetchPaymentStatus();
        setPaymentStatus(status);
      }
    };
    
    loadPaymentStatus();
  }, [isAuthenticated, user]);

  // Function to fetch payment status
  const fetchPaymentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('❌ No token found for payment status check');
        return null;
      }

      const response = await axios.get('/api/dashboard/data', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        const responseData = response.data;
        const data = responseData.data || responseData;
        
        // Check payment status from multiple sources
        const mongoPaymentStatus = data?.dashboardStages?.payment?.status;
        const stage1PaymentStatus = data?.applicationDetails?.stage1?.paymentStatus;
        const zohoPaymentStatus = data?.stage1?.Payment_Status;
        
        if (mongoPaymentStatus === 'completed' || stage1PaymentStatus === 'completed' || zohoPaymentStatus === 'Completed') {
          console.log('✅ Payment status: COMPLETED');
          return 'completed';
        }
        
        console.log('⏳ Payment status: PENDING');
        return 'pending';
    } else {
        console.log('❌ Failed to fetch payment status:', response.status);
        return null;
      }
    } catch (error) {
      console.error('❌ Error fetching payment status:', error);
      return null;
    }
  };

  // Function to clear token and redirect to login
  const clearTokenAndRedirect = () => {
    console.log('🚨 clearTokenAndRedirect: Function called');
    console.log('🚨 clearTokenAndRedirect: Current isRedirecting state:', isRedirecting);
    
    if (isRedirecting) {
      console.log('🔄 Already redirecting, skipping duplicate redirect');
      return;
    }
    
    console.log('🔒 Clearing old token and redirecting to login');
    console.log('🚨 REDIRECTING TO: /login/parent');
    setIsRedirecting(true);
    localStorage.removeItem('token');
    
    // Use navigate instead of window.location.href for better React Router integration
    navigate('/login/parent');
  };

  // Function to refresh authentication state without redirecting
  const refreshAuthState = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in again.');
        return;
      }

      // Skip token validation to prevent 429 errors
        setError('');
        setSuccess('Authentication refreshed successfully!');
        // Reload data
        loadExistingData();
    } catch (error) {
      console.error('❌ Error refreshing auth state:', error);
      setError('Failed to refresh authentication. Please try again.');
    }
  };

  // Function to retry API calls with exponential backoff
  const retryApiCall = async (apiCall, maxRetries = 3, baseDelay = 1000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Attempt ${attempt}/${maxRetries}`);
        return await apiCall();
      } catch (error) {
        console.error(`❌ Attempt ${attempt} failed:`, error);
        
        if (attempt === maxRetries) {
          throw error; // Re-throw on final attempt
        }
        
        // Exponential backoff
        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.log(`⏳ Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  // Function to check authentication state and provide debugging info
  const checkAuthState = () => {
    const token = localStorage.getItem('token');
    const authContextState = { user, isAuthenticated, loading: false };
    
    console.log('🔍 Auth State Check:', {
      localStorageToken: token ? `${token.substring(0, 20)}...` : 'null',
      authContextState,
      currentPath: window.location.pathname,
      timestamp: new Date().toISOString()
    });
    
    // Also test the token with the backend
    if (token) {
      axios.get('/api/auth/test-token', {
        headers: {
          'x-auth-token': token
        }
      })
      .then(response => response.data)
      .then(data => {
        console.log('🔍 Backend token test result:', data);
      })
      .catch(error => {
        console.error('❌ Backend token test failed:', error);
      });
    }
    
    return { token, authContextState };
  };

  // Function to validate token before making API calls
  const validateTokenBeforeCall = async (token) => {
    try {
      console.log('🔍 Validating token:', token ? `${token.substring(0, 20)}...` : 'null');
      
      const testResponse = await axios.get('/api/auth/test-token', {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (testResponse.status === 200) {
        const result = testResponse.data;
        if (result.valid) {
          console.log('✅ Token validation successful:', result.message);
          return true;
        } else {
          console.log('❌ Token validation failed:', result.error);
          return false;
        }
      } else {
        const errorData = await testResponse.json();
        console.log('❌ Token validation failed with status:', testResponse.status, errorData);
        return false;
      }
    } catch (error) {
      console.error('❌ Token validation error:', error);
      return false;
    }
  };

  // Function to check if we should actually redirect to login
  const shouldRedirectToLogin = (error, status) => {
    // Only redirect on actual authentication failures, not on network or server errors
    if (status === 401) {
      return true; // Authentication error
    }
    
    // Don't redirect on network errors or server errors
    if (error?.message?.includes('Network Error') || 
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('ECONNREFUSED') ||
        status >= 500) {
      return false;
    }
    
    return false;
  };

  // Function to retry form submission with exponential backoff
  const retrySubmission = async (attempt = 1, maxAttempts = 3) => {
    if (attempt > maxAttempts) {
      setError('Maximum retry attempts reached. Please try again later.');
      return;
    }

    console.log(`🔄 Retrying form submission (attempt ${attempt}/${maxAttempts})`);
    
    // Exponential backoff: wait 1s, 2s, 4s
    const delay = Math.pow(2, attempt - 1) * 1000;
    
    setTimeout(async () => {
      try {
        await handleSubmit();
      } catch (error) {
        console.error(`❌ Retry attempt ${attempt} failed:`, error);
        if (attempt < maxAttempts) {
          retrySubmission(attempt + 1, maxAttempts);
        }
      }
    }, delay);
  };

  // Helper function to check if form data has meaningful values (excluding default values)
  const hasValidFormData = (formData) => {
    if (!formData || typeof formData !== 'object' || Object.keys(formData).length === 0) {
      return false;
    }
    
    // Define default values that buildFormDataFromZohoCRM sets
    const defaultValues = {
      preferredCampus: 'Bangalore',
      curriculum: 'CBSE',
      session: '2026-27',
      fatherCountryCode: '91',
      motherCountryCode: '91',
      guardianCountryCode: '91',
      applicationStage1Filled: 'Yes'
    };
    
    // Check if at least some critical fields have non-empty, non-default values
    // We only check fields that should come from actual Zoho data, not defaults
    const criticalFields = [
      'studentFirstName',
      'studentLastName',
      'gradeApplying'
    ];
    
    const hasValidData = criticalFields.some(field => {
      const value = formData[field];
      // Check: value exists, is not null/undefined, is not empty string, is not just whitespace
      const isValid = value !== null && value !== undefined && value !== '' && String(value).trim() !== '';
      return isValid;
    });
    
    console.log('🔍 hasValidFormData check:', {
      hasKeys: Object.keys(formData).length > 0,
      hasValidData,
      sampleValues: {
        studentFirstName: formData.studentFirstName,
        studentLastName: formData.studentLastName,
        gradeApplying: formData.gradeApplying
      },
      fieldChecks: criticalFields.map(field => ({
        field,
        value: formData[field],
        isValid: formData[field] !== null && formData[field] !== undefined && formData[field] !== '' && String(formData[field]).trim() !== ''
      }))
    });
    
    return hasValidData;
  };

  // Function to build form data directly from Zoho CRM data based on leadId/applicationId
  const buildFormDataFromZohoCRM = (zohoData) => {
    console.log('🔧 buildFormDataFromZohoCRM: Building form data from Zoho CRM data');
    console.log('🔧 Zoho data structure:', zohoData);
    console.log('🔧 Zoho data keys:', Object.keys(zohoData || {}));
    console.log('🔧 Zoho data type:', typeof zohoData);
    console.log('🔧 Zoho data is null/undefined:', zohoData == null);
    
    // Enhanced debugging for the specific issue
    console.log('🔧 DEBUG: Detailed zohoData analysis:', {
      hasFormData: !!zohoData?.formData,
      hasLeadData: !!zohoData?.leadData,
      hasApplicationData: !!zohoData?.applicationData,
      leadId: zohoData?.leadId,
      applicationId: zohoData?.applicationId,
      leadStage: zohoData?.leadStage,
      fromModule: zohoData?.fromModule,
      formDataKeys: zohoData?.formData ? Object.keys(zohoData.formData) : null,
      leadDataKeys: zohoData?.leadData ? Object.keys(zohoData.leadData) : null,
      applicationDataKeys: zohoData?.applicationData ? Object.keys(zohoData.applicationData) : null
    });
    
    // Return empty object if no data
    if (!zohoData || typeof zohoData !== 'object') {
      console.log('🔧 No valid zohoData provided, returning empty form data');
      return {};
    }
    
    // Extract form data from Zoho CRM response
    const formData = zohoData?.formData || {};
    const leadData = zohoData?.leadData || {};
    const applicationData = zohoData?.applicationData || {};
    
    // Create comprehensive mapping with all required fields
    const completeFormData = {
      // Campus & Curriculum (from Zoho or defaults)
      preferredCampus: formData.preferredCampus || leadData.Preferred_Campus || 'Bangalore',
      curriculum: formData.curriculum || leadData.Board || 'CBSE',
      
      // Student Information (from Zoho form data)
      studentFirstName: formData.studentFirstName || leadData.Student_First_Name || leadData.First_Name || '',
      studentMiddleName: formData.studentMiddleName || leadData.Student_Middle_Name || leadData.Middle_Name || '',
      studentLastName: formData.studentLastName || leadData.Student_Last_Name || leadData.Last_Name || '',
      gender: formData.gender || leadData.Gender || '',
      dateOfBirth: formData.dateOfBirth || leadData.Date_of_Birth || leadData.DOB || '',
      motherTongue: formData.motherTongue || leadData.Mother_Tongue || '',
      otherMotherTongue: formData.otherMotherTongue || leadData.Other_Mother_Tongue || '',
      gradeApplying: formData.gradeApplying || leadData.Grade_Applying || leadData.Class_Looking_For || '',
      session: formData.session || leadData.Session || '2026-27',
      nationality: formData.nationality || leadData.Nationality_of_Student || leadData.Nationality || '',
      religion: formData.religion || leadData.Religion || '',
      otherReligion: formData.otherReligion || leadData.Other_Religion || '',
      caste: formData.caste || leadData.Caste || '',
      casteCertificateName: formData.casteCertificateName || leadData.Name_of_Caste_on_Certificate || '',

      // Location
      address: {
        country: formData.address?.country || leadData.Country || '',
        state: formData.address?.state || leadData.State || '',
        city: formData.address?.city || leadData.City || '',
        otherCity: formData.address?.otherCity || ''
      },
      
      // Father Information (from Zoho form data)
      fatherFirstName: formData.fatherFirstName || leadData.Father_First_Name || leadData.Father_Name || '',
      fatherMiddleName: formData.fatherMiddleName || leadData.Father_Middle_Name || '',
      fatherLastName: formData.fatherLastName || leadData.Father_Last_Name || '',
      fatherCountryCode: formData.fatherCountryCode || leadData.Father_Country_Code || '91',
      fatherPhoneNumber: formData.fatherPhoneNumber || leadData.Father_Phone_Number || leadData.Mobile || '',
      fatherEmail: formData.fatherEmail || leadData.Father_Email_ID || leadData.Email || '',
      
      // Mother Information (from Zoho form data)
      motherFirstName: formData.motherFirstName || leadData.Mother_First_Name || leadData.Mother_Name || '',
      motherMiddleName: formData.motherMiddleName || leadData.Mother_Middle_Name || '',
      motherLastName: formData.motherLastName || leadData.Mother_Last_Name || '',
      motherCountryCode: formData.motherCountryCode || leadData.Mother_Country_Code || '91',
      motherPhoneNumber: formData.motherPhoneNumber || leadData.Mother_Phone_Number || leadData.Secondary_Mobile_No || '',
      motherEmail: formData.motherEmail || leadData.Mother_Email_ID || leadData.Secondary_Email || '',
      
      // Guardian Information (from Zoho form data)
      guardianFirstName: formData.guardianFirstName || leadData.Guardian_First_Name || '',
      guardianMiddleName: formData.guardianMiddleName || leadData.Guardian_Middle_Name || '',
      guardianLastName: formData.guardianLastName || leadData.Guardian_Last_Name || '',
      guardianCountryCode: formData.guardianCountryCode || leadData.Guardian_Country_Code || '91',
      guardianPhoneNumber: formData.guardianPhoneNumber || leadData.Guardian_Phone_Number || '',
      guardianEmail: formData.guardianEmail || leadData.Guardian_Email_ID || '',
      guardianRelationship: formData.guardianRelationship || leadData.Guardian_Relationship || '',
      
      // Family Information (from Zoho form data)
      familyStructure: formData.familyStructure || leadData.Family_Structure || '',
      maritalStatus: formData.maritalStatus || leadData.Marital_Status || '',
      singleParentMaritalStatus: formData.singleParentMaritalStatus || leadData.Single_Parent_Marital_Status || '',
      
      // Legacy fields for backward compatibility
      firstName: formData.studentFirstName || leadData.Student_First_Name || leadData.First_Name || '',
      lastName: formData.studentLastName || leadData.Student_Last_Name || leadData.Last_Name || '',
      email: formData.fatherEmail || leadData.Father_Email_ID || leadData.Email || '',
      gradeApplied: formData.gradeApplying || leadData.Grade_Applying || leadData.Class_Looking_For || '',
      
      // Application Stage 1 Status from Zoho CRM
      applicationStage1Filled: formData.applicationStage1Filled || leadData.Application_Stage_1_Filled || 'Yes',
      
      // Module information from Zoho CRM
      fromModule: zohoData.fromModule || 'Leads'
    };
    
    // Clean up empty strings and set defaults where needed
    Object.keys(completeFormData).forEach(key => {
      if (completeFormData[key] === '' || completeFormData[key] === null || completeFormData[key] === undefined) {
        // Set appropriate defaults for required fields
        if (key === 'preferredCampus') completeFormData[key] = 'Bangalore';
        else if (key === 'curriculum') completeFormData[key] = 'CBSE';
        else if (key === 'session') completeFormData[key] = '2026-27';
        else if (key === 'fatherCountryCode' || key === 'motherCountryCode' || key === 'guardianCountryCode') {
          completeFormData[key] = '91';
        } else if (key === 'applicationStage1Filled') {
          completeFormData[key] = 'Yes';
        } else {
          completeFormData[key] = '';
        }
      }
    });
    
    console.log('🔧 Complete form data mapping created:', {
      totalFields: Object.keys(completeFormData).length,
      sampleFields: {
        preferredCampus: completeFormData.preferredCampus,
        studentFirstName: completeFormData.studentFirstName,
        studentLastName: completeFormData.studentLastName,
        fatherPhoneNumber: completeFormData.fatherPhoneNumber,
        motherPhoneNumber: completeFormData.motherPhoneNumber,
        applicationStage1Filled: completeFormData.applicationStage1Filled,
        fromModule: completeFormData.fromModule
      }
    });
    
    return completeFormData;
  };

  // Cache for Zoho data to prevent multiple API calls
  const zohoDataCache = useRef({ data: null, timestamp: 0 });
  const CACHE_DURATION = 30000; // 30 seconds cache

  // Function to fetch latest Zoho CRM data with caching
  const fetchLatestZohoData = async (token) => {
    const now = Date.now();
    
    // Return cached data if it's still fresh
    if (zohoDataCache.current.data && (now - zohoDataCache.current.timestamp) < CACHE_DURATION) {
      console.log('📋 Using cached Zoho data (age:', now - zohoDataCache.current.timestamp, 'ms)');
      return zohoDataCache.current.data;
    }

    try {
      console.log('🔄 Fetching latest Zoho CRM data from API...');
      
      const response = await axios.post('/api/auth/refresh-zoho-data', {}, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      });
      
      if (response.status === 200) {
        const result = response.data;
        console.log('✅ Latest Zoho CRM data fetched successfully:', result);
        
        // Cache the data
        zohoDataCache.current = {
          data: result.zohoData,
          timestamp: now
        };
        
        return result.zohoData;
      } else {
        console.log('⚠️ Failed to fetch latest Zoho data, using cached data');
        return zohoDataCache.current.data || null;
      }
    } catch (error) {
      console.log('⚠️ Error fetching latest Zoho data:', error.message);
      return zohoDataCache.current.data || null;
    }
  };

  // Prevent multiple simultaneous data loads
  const isLoadingData = useRef(false);

  const loadExistingData = async () => {
    console.log('🚀 loadExistingData: Function called at:', new Date().toISOString());
    console.log('🚀 loadExistingData: Function is executing...');
    
    if (isRedirecting) {
      console.log('🔄 Already redirecting, preventing data load');
      return;
    }

    // Prevent multiple simultaneous calls
    if (isLoadingData.current) {
      console.log('🔄 Data load already in progress, skipping duplicate call');
      return;
    }
    
    isLoadingData.current = true;
    
    // Set loading state and data loaded to false when starting to load
    setLoading(true);
    setDataLoaded(false);
    
    try {
      // Check authentication state first
      const { token, authContextState } = checkAuthState();
      
      if (!token) {
        console.log('❌ No token found in localStorage');
        clearTokenAndRedirect();
        return;
      }

      // Skip token validation to prevent 429 errors - trust the auth context
      console.log('✅ Proceeding with data load (skipping token validation to prevent rate limiting)');
      
      // Try multiple data sources to find existing data
      let zohoFormData = null;
      
      console.log('🔍 Stage1Form: Checking for existing data...');
      console.log('🔍 Stage1Form: User object:', user);
      console.log('🔍 Stage1Form: User zohoData:', user?.zohoData);
      
      // Method 1: Try user.zohoData first
      if (user?.zohoData) {
        console.log('📋 Trying user.zohoData...');
        zohoFormData = buildFormDataFromZohoCRM(user.zohoData);
        console.log('📋 Built form data from user.zohoData:', zohoFormData);
      }
      
      // Method 2: Try localStorage if user.zohoData didn't have valid data
      if (!hasValidFormData(zohoFormData)) {
        console.log('📋 No valid data from user.zohoData, trying localStorage...');
        try {
          const storedZohoData = localStorage.getItem('zohoCRMData');
          if (storedZohoData) {
            const parsedZohoData = JSON.parse(storedZohoData);
            console.log('📋 Zoho CRM data found in localStorage:', parsedZohoData);
            zohoFormData = buildFormDataFromZohoCRM(parsedZohoData);
            console.log('📋 Built form data from localStorage:', zohoFormData);
          }
        } catch (error) {
          console.log('📋 No valid Zoho CRM data in localStorage');
        }
      }
      
      // Method 3: Try API if no valid data found yet
      if (!hasValidFormData(zohoFormData)) {
        console.log('📋 No valid data yet, trying API...');
        try {
          const latestZohoData = await fetchLatestZohoData(token);
          if (latestZohoData) {
            console.log('📋 Fetched latest Zoho data from API');
            zohoFormData = buildFormDataFromZohoCRM(latestZohoData);
            console.log('📋 Built form data from API:', zohoFormData);
          }
        } catch (error) {
          console.log('📋 Failed to fetch from API:', error.message);
        }
      }
      
      console.log('📋 Final zohoFormData:', zohoFormData);
      console.log('📋 Final form data keys:', zohoFormData ? Object.keys(zohoFormData) : 'No data');
      console.log('📋 Sample values:', {
        preferredCampus: zohoFormData?.preferredCampus,
        studentFirstName: zohoFormData?.studentFirstName,
        studentLastName: zohoFormData?.studentLastName
      });
      
      // CRITICAL FIX: Apply Zoho data FIRST, before any API calls (only if valid data exists)
      if (hasValidFormData(zohoFormData)) {
        console.log('🚨 CRITICAL FIX: Applying Zoho data BEFORE API calls');
        console.log('🚨 Zoho data to apply:', zohoFormData);
        console.log('🚨 Zoho data keys:', Object.keys(zohoFormData));
        console.log('🚨 Application Stage 1 Filled:', zohoFormData.applicationStage1Filled);
        
        // Apply Zoho data immediately using safe setter
        console.log('🚨 CRITICAL FIX: About to apply Zoho data to form');
        console.log('🚨 Zoho data to apply:', zohoFormData);
        setFormDataSafely(zohoFormData);
        console.log('🚨 CRITICAL FIX: Zoho data applied immediately');
        
        // Verify the data was applied
        setTimeout(() => {
          console.log('🚨 VERIFICATION: Form data after application:', formData);
        }, 100);
        
        // Set phone numbers if available
        if (zohoFormData.fatherCountryCode && zohoFormData.fatherPhoneNumber) {
          const fatherFullPhone = `+${zohoFormData.fatherCountryCode}${zohoFormData.fatherPhoneNumber}`;
          setFatherPhone(fatherFullPhone);
        }
        
        if (zohoFormData.motherCountryCode && zohoFormData.motherPhoneNumber) {
          const motherFullPhone = `+${zohoFormData.motherCountryCode}${zohoFormData.motherPhoneNumber}`;
          setMotherPhone(motherFullPhone);
        }
        
        console.log('🚨 CRITICAL FIX: Form should now be populated with Zoho data');
        
        // Show success message based on application status
        if (zohoFormData.applicationStage1Filled === 'Yes') {
          setSuccess('Form loaded with your existing application data. You can view and edit your information.');
        } else {
          setSuccess('Form pre-populated with data from your lead information.');
        }
        setZohoDataLoaded(true);
        setZohoDataSource(zohoFormData.fromModule || user?.zohoData?.fromModule || 'Leads');
        
        // Clear loading state
        setLoading(false);
        setDataLoaded(true);
        isLoadingData.current = false; // Reset loading flag
        return; // Exit early, don't do API calls
      }
      
      console.log('📋 No valid Zoho data found after checking all sources');
      console.log('📋 Setting form as loaded with empty data');
      // Set form as loaded even when no data is found to prevent infinite loading
      setDataLoaded(true);
      setLoading(false);
      setSuccess('Form ready for new application. Please fill in all required fields.');
      isLoadingData.current = false; // Reset loading flag
      
    } catch (error) {
      console.error('❌ Error loading existing data:', error);
      setError('Unable to load application data. Please try refreshing the page.');
      // Still set as loaded to prevent infinite loading
      setDataLoaded(true);
      setLoading(false);
      isLoadingData.current = false; // Reset loading flag
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If marital status changes to not "Single parent", clear the single parent status
    if (name === 'maritalStatus' && value !== 'Single parent') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        singleParentMaritalStatus: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;

    if (name === 'addressCountry') {
      setFormData(prev => ({
        ...prev,
        address: {
          country: value,
          state: isIndia(value) ? '' : STATE_NOT_APPLICABLE,
          city: '',
          otherCity: ''
        }
      }));
    } else if (name === 'addressState') {
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          state: value,
          city: '',
          otherCity: ''
        }
      }));
    } else if (name === 'addressCity') {
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          city: value,
          otherCity: value === CITY_OTHER ? prev.address?.otherCity || '' : ''
        }
      }));
    } else if (name === 'otherCity') {
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          otherCity: value
        }
      }));
    }
  };

  // Computed full name for backward compatibility
  const getStudentFullName = () => {
    const parts = [
      formData.studentFirstName,
      formData.studentMiddleName,
      formData.studentLastName
    ].filter(Boolean);
    return parts.join(' ');
  };

  // Get display value for mother tongue
  const getMotherTongueDisplay = () => {
    if (formData.motherTongue === 'Other' && formData.otherMotherTongue) {
      return formData.otherMotherTongue;
    }
    return formData.motherTongue;
  };

  // Get display value for religion
  const getReligionDisplay = () => {
    if (formData.religion === 'Other' && formData.otherReligion) {
      return formData.otherReligion;
    }
    return formData.religion;
  };

  const handlePhoneChange = (phone) => {
    setPhoneNumber(phone);
    
    if (phone) {
      const parsedPhoneNumber = parsePhoneNumber(phone);
      if (parsedPhoneNumber) {
        setDialCode(parsedPhoneNumber.countryCallingCode || '');
        setCountryName(parsedPhoneNumber.country || '');
      }
    } else {
      setDialCode('');
      setCountryName('');
    }
  };

  const handleFatherPhoneChange = (phone) => {
    setFatherPhone(phone);
    
    if (phone) {
      const parsedPhoneNumber = parsePhoneNumber(phone);
      if (parsedPhoneNumber) {
        setFormData(prev => ({
          ...prev,
          fatherCountryCode: parsedPhoneNumber.countryCallingCode || '',
          fatherPhoneNumber: parsedPhoneNumber.nationalNumber || ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        fatherCountryCode: '',
        fatherPhoneNumber: ''
      }));
    }
  };

  const handleMotherPhoneChange = (phone) => {
    setMotherPhone(phone);
    
    if (phone) {
      const parsedPhoneNumber = parsePhoneNumber(phone);
      if (parsedPhoneNumber) {
        setFormData(prev => ({
          ...prev,
          motherCountryCode: parsedPhoneNumber.countryCallingCode || '',
          motherPhoneNumber: parsedPhoneNumber.nationalNumber || ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        motherCountryCode: '',
        motherPhoneNumber: ''
      }));
    }
  };

  const handleGuardianPhoneChange = (phone) => {
    setGuardianPhone(phone);
    
    if (phone) {
      const parsedPhoneNumber = parsePhoneNumber(phone);
      if (parsedPhoneNumber) {
        setFormData(prev => ({
          ...prev,
          guardianCountryCode: parsedPhoneNumber.countryCallingCode || '',
          guardianPhoneNumber: parsedPhoneNumber.nationalNumber || ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        guardianCountryCode: '',
        guardianPhoneNumber: ''
      }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      console.log('🔑 Token for save:', token);
      console.log('🔍 Sending form data:', { ...formData, mobile: phoneNumber });
      
      const response = await axios.post('/api/application/stage1', {
        ...formData,
        mobile: phoneNumber,
        // Include the specific single parent status if applicable
        maritalStatus: formData.maritalStatus === 'Single parent' 
          ? `${formData.maritalStatus} - ${formData.singleParentMaritalStatus}`
          : formData.maritalStatus,
        // Include computed full name for backward compatibility
        studentFullName: getStudentFullName()
      }, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      });

      console.log('📡 Save response status:', response.status);
      console.log('📡 Save response headers:', Object.fromEntries(response.headers.entries()));

      if (response.status === 200) {
        const responseData = response.data;
        console.log('✅ Save response data:', responseData);
        setSuccess('Stage 1 application saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        console.log('❌ Save failed with status:', response.status);
        
        // Get error data from axios response
        const errorData = response.data || { error: 'Unknown error occurred', status: response.status };
        
        console.error('❌ Backend error:', errorData);
        
        if (errorData.details && errorData.details.length > 0) {
          // Show validation errors
          const errorMessages = errorData.details.map(err => `${err.path}: ${err.msg}`).join(', ');
          setError(`Validation failed: ${errorMessages}`);
        } else {
          setError(errorData.error || errorData.message || `Backend error (${response.status})`);
        }
      }
    } catch (error) {
      console.error('❌ Network error:', error);
      setError('Failed to save application. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    // Prevent default form submission behavior
    if (e) {
      e.preventDefault();
      console.log('🚀 handleSubmit: Default form submission prevented');
    }
    
    console.log('🚀 handleSubmit: Function called');
    console.log('🚀 handleSubmit: Form validation starting...');
    
    if (!validateForm()) {
      console.log('❌ Form validation failed');
      return;
    }
    
    if (isRedirecting) {
      console.log('🔄 Already redirecting, preventing form submission');
      return;
    }

    console.log('🚀 handleSubmit: Starting form submission...');
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      console.log('🔑 Token for submit:', token);
      console.log('🔍 Submitting form data:', { ...formData, mobile: phoneNumber });
      
      console.log('🔑 handleSubmit: About to validate token...');
      
      // Skip token validation to prevent 429 errors
      console.log('✅ Proceeding with integrated update (skipping token validation)');
      
      // Debug: Check user and zohoData
      console.log('🔍 Debug - User object:', user);
      console.log('🔍 Debug - User zohoData:', user?.zohoData);
      console.log('🔍 Debug - Form data:', formData);
      
      // Check if we have the required Zoho data
      if (!user?.zohoData) {
        console.log('❌ No Zoho data available - this might be the issue!');
        setError('No Zoho CRM data available. Please try logging in again.');
        return;
      }
      
      if (!user.zohoData.leadId && !user.zohoData.applicationId) {
        console.log('❌ Missing both leadId and applicationId in Zoho data');
        setError('Incomplete Zoho CRM data. Please try logging in again.');
        return;
      }
      
      console.log('✅ Zoho data validation passed, proceeding with integrated update');
      console.log('🚀 About to call updateZohoCRMAndMongoDB() function...');
      
      // Debug: Log the exact data being sent
      console.log('🔍 Final form data being sent:', {
        formData: formData,
        phoneNumber: phoneNumber,
        userZohoData: user?.zohoData
      });
      
      // Debug: Check if required Zoho fields are present
      console.log('🔍 Required Zoho fields check:', {
        hasFromModule: !!user?.zohoData?.fromModule,
        fromModule: user?.zohoData?.fromModule,
        hasLeadId: !!user?.zohoData?.leadId,
        leadId: user?.zohoData?.leadId,
        hasApplicationId: !!user?.zohoData?.applicationId,
        applicationId: user?.zohoData?.applicationId
      });
      
      // Update both Zoho CRM and MongoDB
      const result = await updateZohoCRMAndMongoDB();
      console.log('🚀 updateZohoCRMAndMongoDB() function completed with result:', result);
      
      if (result.success) {
        console.log('✅ Integrated update successful:', result);

        // Persist address into localStorage so it survives a same-session refresh
        try {
          const stored = localStorage.getItem('zohoCRMData');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (!parsed.formData) parsed.formData = {};
            parsed.formData.address = {
              country: formData.address?.country || '',
              state: formData.address?.state || '',
              city: formData.address?.city || '',
              otherCity: formData.address?.otherCity || ''
            };
            localStorage.setItem('zohoCRMData', JSON.stringify(parsed));
          }
        } catch (_) {}

        // Check payment status before redirecting
        const currentPaymentStatus = await fetchPaymentStatus();
        setPaymentStatus(currentPaymentStatus);
        
        if (currentPaymentStatus === 'completed') {
          setSuccess('Application saved successfully! Payment already completed.');
          // Redirect to dashboard instead of payment
          setTimeout(() => {
            navigate('/dashboard');
          }, 1500);
        } else {
          setSuccess('Application saved successfully! Redirecting to payment...');
          // Redirect to payment only if not completed
        setTimeout(() => {
          navigate('/payment');
        }, 1500);
        }
        
        // Reset redirecting state on success
        setIsRedirecting(false);
      } else {
        console.error('❌ Integrated update failed:', result.error);
        setError(`Failed to save application: ${result.error}`);
      }
      
    } catch (error) {
      console.error('❌ Error during form submission:', error);
      
      // Check if it's a network/backend connection issue
      if (error.message?.includes('Network Error') || error.message?.includes('Failed to fetch')) {
        console.log('🌐 Network/Backend connection issue detected');
        
        // In development mode, show a helpful message instead of redirecting
        if (process.env.NODE_ENV === 'development') {
          setError('Backend connection failed. Please ensure the server is running and try again.');
        } else {
          setError('Network error. Please check your connection and try again.');
        }
      } else {
        setError('Failed to submit application. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to update both Zoho CRM and MongoDB
  const updateZohoCRMAndMongoDB = async () => {
    try {
      console.log('🔄 Updating both Zoho CRM and MongoDB with form data...');
      
      // Check if we have Zoho data to update
      if (!user?.zohoData) {
        console.log('⚠️ No Zoho data available, cannot update CRM');
        console.log('🔍 User object:', user);
        console.log('🔍 User zohoData:', user?.zohoData);
        return {
          success: false,
          error: 'No Zoho CRM data available for this user'
        };
      }
      
      const zohoData = user.zohoData;
      console.log('📋 Zoho data for integrated update:', zohoData);
      
      // Check if required fields are present
      if (!zohoData.leadId && !zohoData.applicationId) {
        console.log('⚠️ Missing both leadId and applicationId in Zoho data');
        return {
          success: false,
          error: 'Missing lead ID or application ID in Zoho CRM data'
        };
      }
      
      // Prepare comprehensive data for both Zoho CRM and MongoDB
      const requestData = {
        // Lead/Application identification
        leadId: zohoData.leadId,
        applicationId: zohoData.applicationId,
        fromModule: zohoData.fromModule,
        
        // Form data from Stage 1
        preferredCampus: formData.preferredCampus,
        curriculum: formData.curriculum,
        studentFirstName: formData.studentFirstName,
        studentMiddleName: formData.studentMiddleName,
        studentLastName: formData.studentLastName,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth,
        motherTongue: formData.motherTongue,
        otherMotherTongue: formData.otherMotherTongue,
        gradeApplying: formData.gradeApplying,
        session: formData.session,
        nationality: formData.nationality,
        religion: formData.religion,
        otherReligion: formData.otherReligion,
        caste: formData.caste,
        casteCertificateName: formData.casteCertificateName,
        familyStructure: formData.familyStructure,
        maritalStatus: formData.maritalStatus,
        singleParentMaritalStatus: formData.singleParentMaritalStatus,
        
        // Father Information
        fatherFirstName: formData.fatherFirstName,
        fatherMiddleName: formData.fatherMiddleName,
        fatherLastName: formData.fatherLastName,
        fatherPhoneNumber: formData.fatherPhoneNumber,
        fatherEmail: formData.fatherEmail,
        fatherCountryCode: formData.fatherCountryCode,
        fatherOccupation: formData.fatherOccupation || '',
        
        // Mother Information
        motherFirstName: formData.motherFirstName,
        motherMiddleName: formData.motherMiddleName,
        motherLastName: formData.motherLastName,
        motherPhoneNumber: formData.motherPhoneNumber,
        motherEmail: formData.motherEmail,
        motherCountryCode: formData.motherCountryCode,
        motherOccupation: formData.motherOccupation || '',
        
        // Guardian Information
        guardianFirstName: formData.guardianFirstName,
        guardianMiddleName: formData.guardianMiddleName,
        guardianLastName: formData.guardianLastName,
        guardianPhoneNumber: formData.guardianPhoneNumber,
        guardianEmail: formData.guardianEmail,
        guardianCountryCode: formData.guardianCountryCode,
        guardianRelationship: formData.guardianRelationship,
        
        // Additional fields for MongoDB
        mobile: phoneNumber,
        studentFullName: getStudentFullName(),
        
        // Optional fields that might be needed
        previousSchool: formData.previousSchool || '',
        specialNeeds: formData.specialNeeds || 'None',
        medicalConditions: formData.medicalConditions || '',
        emergencyContactName: formData.emergencyContactName || '',
        emergencyContactRelation: formData.emergencyContactRelation || '',
        emergencyContactMobile: formData.emergencyContactMobile || '',
        
        // Address fields
        address: {
          street: formData.address?.street || '',
          city: getResolvedCityValue(formData.address?.city, formData.address?.otherCity),
          state: isIndia(formData.address?.country)
            ? formData.address?.state || ''
            : STATE_NOT_APPLICABLE,
          country: formData.address?.country || '',
          pincode: formData.address?.pincode || ''
        }
      };
      
      console.log('📤 Sending comprehensive data to backend for integrated update:', requestData);
      
      // Test backend connectivity first
      try {
        console.log('🧪 Testing backend connectivity...');
        const healthResponse = await axios.get('/api/auth/health', {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        console.log('🏥 Backend health check response:', healthResponse.status);
      } catch (healthError) {
        console.log('⚠️ Backend health check failed:', healthError.message);
      }
      
      // Call the integrated backend endpoint
      const response = await axios.post('/api/application/stage1-integrated', requestData, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': localStorage.getItem('token')
        }
      });
      
      console.log('📡 Backend response status:', response.status);
      console.log('📡 Backend response headers:', response.headers);
      
      if (response.status === 200) {
        const result = response.data;
        console.log('✅ Backend integrated update response:', result);
        
        if (result.success) {
          console.log('🎉 Record updated successfully in both Zoho CRM and MongoDB');
          return {
            success: true,
            message: 'Application saved successfully in both Zoho CRM and MongoDB',
            crmResponse: result.crmResponse,
            mongoResponse: result.mongoResponse
          };
        } else {
          console.log('⚠️ Backend integrated update failed:', result.error);
          return {
            success: false,
            error: result.error || 'Integrated update failed'
          };
        }
      } else {
        console.log('❌ Backend API call failed:', response.status);
        const errorText = await response.text();
        console.log('❌ Error response:', errorText);
        
        return {
          success: false,
          error: `Backend API error: ${response.status} - ${errorText}`
        };
      }
      
    } catch (error) {
      console.error('❌ Error updating integrated system:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      return {
        success: false,
        error: `Network error: ${error.message}`
      };
    }
  };

  const validateForm = () => {
    // Campus & Curriculum validation - these are pre-filled and non-editable
    // No validation needed as they have default values
    if (!formData.session) {
      setError('Please select a session');
      return false;
    }
    if (!formData.gradeApplying) {
      setError('Please select a grade');
      return false;
    }

    // Student Information validation
    if (!formData.studentFirstName.trim()) {
      setError('Please enter the student first name');
      return false;
    }
    if (!formData.studentLastName.trim()) {
      setError('Please enter the student last name');
      return false;
    }
    if (!formData.gender) {
      setError('Please select the gender');
      return false;
    }
    if (!formData.dateOfBirth) {
      setError('Please enter the date of birth');
      return false;
    }
    if (!formData.motherTongue) {
      setError('Please select the mother tongue');
      return false;
    }
    if (formData.motherTongue === 'Other' && !formData.otherMotherTongue?.trim()) {
      setError('Please specify the other mother tongue');
      return false;
    }
    if (!formData.nationality) {
      setError('Please select the nationality');
      return false;
    }
    if (!formData.address?.country) {
      setError('Please select the country');
      return false;
    }
    if (isIndia(formData.address.country) && !formData.address.state) {
      setError('Please select the state');
      return false;
    }
    if (!formData.address.city) {
      setError('Please select the city');
      return false;
    }
    if (formData.address.city === CITY_OTHER && !formData.address.otherCity?.trim()) {
      setError('Please specify your city');
      return false;
    }
    if (!formData.religion) {
      setError('Please select the religion');
      return false;
    }
    if (formData.religion === 'Other' && !formData.otherReligion?.trim()) {
      setError('Please specify the other religion');
      return false;
    }
    if (!formData.caste) {
      setError('Please select the caste');
      return false;
    }
    if ((formData.caste === 'SC' || formData.caste === 'ST' || formData.caste === 'OBC') && !formData.casteCertificateName.trim()) {
      setError('Please enter the name of caste as mentioned on caste certificate');
      return false;
    }

    // Family Structure validation
    if (!formData.familyStructure) {
      setError('Please select your family structure');
      return false;
    }
    
    // Marital Status validation
    if (!formData.maritalStatus) {
      setError('Please select your marital status');
      return false;
    }
    
    // If single parent, validate the specific single parent option
    if (formData.maritalStatus === 'Single parent') {
      if (!formData.singleParentMaritalStatus) {
        setError('Please select a specific option for single parent marital status');
        return false;
      }
    }

    // Father Information validation (conditional based on family structure)
    if (!['Single parent - Mother only', 'Parents not present - Child living with Guardian'].includes(formData.familyStructure)) {
      if (!formData.fatherFirstName.trim()) {
        setError('Please enter father\'s first name');
        return false;
      }
      if (!formData.fatherLastName.trim()) {
        setError('Please enter father\'s last name');
        return false;
      }
      if (!formData.fatherPhoneNumber.trim()) {
        setError('Please enter father\'s contact number');
        return false;
      }
      if (!formData.fatherEmail.trim()) {
        setError('Please enter father\'s email address');
        return false;
      }
      if (!/\S+@\S+\.\S+/.test(formData.fatherEmail)) {
        setError('Please enter a valid email address for father');
        return false;
      }
    }

    // Mother Information validation (conditional based on family structure)
    if (!['Single parent - Father only', 'Parents not present - Child living with Guardian'].includes(formData.familyStructure)) {
      if (!formData.motherFirstName.trim()) {
        setError('Please enter mother\'s first name');
        return false;
      }
      if (!formData.motherLastName.trim()) {
        setError('Please enter mother\'s last name');
        return false;
      }
      if (!formData.motherPhoneNumber.trim()) {
        setError('Please enter mother\'s contact number');
        return false;
      }
      if (!formData.motherEmail.trim()) {
        setError('Please enter mother\'s email address');
        return false;
      }
      if (!/\S+@\S+\.\S+/.test(formData.motherEmail)) {
        setError('Please enter a valid email address for mother');
        return false;
      }
    }

    // Guardian Information validation (when parents are not present)
    if (formData.familyStructure === 'Parents not present - Child living with Guardian') {
      if (!formData.guardianFirstName.trim()) {
        setError('Please enter guardian\'s first name');
        return false;
      }
      if (!formData.guardianLastName.trim()) {
        setError('Please enter guardian\'s last name');
        return false;
      }
      if (!formData.guardianPhoneNumber.trim()) {
        setError('Please enter guardian\'s contact number');
        return false;
      }
      if (!formData.guardianEmail.trim()) {
        setError('Please enter guardian\'s email address');
        return false;
      }
      if (!/\S+@\S+\.\S+/.test(formData.guardianEmail)) {
        setError('Please enter a valid email address for guardian');
        return false;
      }
      if (!formData.guardianRelationship) {
        setError('Please select guardian\'s relationship to student');
        return false;
      }
    }
    
    setError('');
    return true;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Stage 1 Application" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Form Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Stage 1 Application</h1>
            <p className="text-lg text-gray-600 mb-6">
              Please provide the basic information for your child's admission application
            </p>
            
            {/* Refresh Data Button */}
            <div className="flex justify-center mb-4">
              <button
                type="button"
                onClick={() => {
                  console.log('🔄 Manual refresh triggered');
                  setDataLoaded(false);
                  loadExistingData();
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
                disabled={loading}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>{loading ? 'Refreshing...' : 'Refresh Data'}</span>
              </button>
            </div>
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  1
                </div>
                <span>Basic Information</span>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gray-200 text-gray-500 rounded-full flex items-center justify-center text-sm font-bold">
                  2
                </div>
                <span>Payment</span>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gray-200 text-gray-500 rounded-full flex items-center justify-center text-sm font-bold">
                  3
                </div>
                <span>Detailed Information</span>
              </div>
            </div>
          </div>
        </div>

        {/* Zoho CRM Data Banner */}
        {zohoDataLoaded && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center">
                  <FileText className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Form Pre-populated with Existing Data
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  Your form has been automatically filled with data from your previous {zohoDataSource === 'Leads' ? 'lead information' : 'application'} in our system.
                  You can review and edit any fields as needed.
                </p>
              </div>
            </div>
            
          </div>
        )}


        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {!dataLoaded ? (
            // Loading state while data is being fetched and populated
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Application Data</h3>
              <p className="text-gray-600 text-center max-w-md">
                Please wait while we load your existing application data and pre-populate the form...
              </p>
            </div>
          ) : (
            // Form content when data is loaded
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Campus and Curriculum Section */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <MapPin className="h-5 w-5 text-teal-600 mr-2" />
                Campus & Curriculum
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="preferredCampus" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Campus <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="preferredCampus"
                    name="preferredCampus"
                    value={formData.preferredCampus}
                    className="form-input bg-gray-100 cursor-not-allowed"
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">This field is pre-selected and cannot be changed</p>
                </div>

                <div>
                  <label htmlFor="curriculum" className="block text-sm font-medium text-gray-700 mb-2">
                    Board <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="curriculum"
                    name="curriculum"
                    value={formData.curriculum}
                    className="form-input bg-gray-100 cursor-not-allowed"
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">This field is pre-selected and cannot be changed</p>
                </div>
              </div>
            </div>

            {/* Student Information Section */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 text-teal-600 mr-2" />
                Student Information
              </h2>
              
              {/* Display computed full name */}
              {formData.studentFirstName && formData.studentLastName && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">Full Name:</span> {getStudentFullName()}
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="studentFirstName" className="block text-sm font-medium text-gray-700 mb-2">
                    Student First Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="studentFirstName"
                      name="studentFirstName"
                      value={formData.studentFirstName}
                      onChange={handleChange}
                      className="form-input pl-12"
                      placeholder="Enter student's first name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="studentMiddleName" className="block text-sm font-medium text-gray-700 mb-2">
                    Student Middle Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="studentMiddleName"
                      name="studentMiddleName"
                      value={formData.studentMiddleName}
                      onChange={handleChange}
                      className="form-input pl-12"
                      placeholder="Enter student's middle name (optional)"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="studentLastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Student Last Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="studentLastName"
                      name="studentLastName"
                      value={formData.studentLastName}
                      onChange={handleChange}
                      className="form-input pl-12"
                      placeholder="Enter student's last name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    options={genders}
                    placeholder="Select Gender"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth as mentioned on Birth Certificate <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      className="form-input pl-12"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="nationality" className="block text-sm font-medium text-gray-700 mb-2">
                    Nationality of the Student <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    id="nationality"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    options={nationalities}
                    placeholder="Select Nationality"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="addressCountry" className="block text-sm font-medium text-gray-700 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    id="addressCountry"
                    name="addressCountry"
                    value={formData.address?.country || ''}
                    onChange={handleAddressChange}
                    options={countries.map(c => c.name)}
                    placeholder={locationDataLoading ? 'Loading countries...' : 'Select Country'}
                    required
                    disabled={locationDataLoading}
                  />
                </div>

                <div>
                  <label htmlFor="addressState" className="block text-sm font-medium text-gray-700 mb-2">
                    State / Union Territory{' '}
                    {isIndia(formData.address?.country) && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  {isIndia(formData.address?.country) ? (
                    <SearchableSelect
                      id="addressState"
                      name="addressState"
                      value={formData.address?.state || ''}
                      onChange={handleAddressChange}
                      options={indianStates.map(s => s.name)}
                      placeholder="Select State / Union Territory"
                      required
                      disabled={!formData.address?.country || locationDataLoading}
                    />
                  ) : (
                    <input
                      type="text"
                      id="addressState"
                      name="addressState"
                      value={formData.address?.country ? STATE_NOT_APPLICABLE : ''}
                      className="form-input bg-gray-100 cursor-not-allowed"
                      readOnly
                      disabled
                      placeholder={formData.address?.country ? STATE_NOT_APPLICABLE : 'Select country first'}
                    />
                  )}
                </div>

                <div>
                  <label htmlFor="addressCity" className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    id="addressCity"
                    name="addressCity"
                    value={formData.address?.city || ''}
                    onChange={handleAddressChange}
                    options={availableCities}
                    placeholder={
                      !formData.address?.country
                        ? 'Select country first'
                        : isIndia(formData.address.country) && !formData.address.state
                        ? 'Select state first'
                        : 'Select City'
                    }
                    required
                    disabled={!cityDropdownReady || locationDataLoading}
                  />
                </div>

                {formData.address?.city === CITY_OTHER && (
                  <div>
                    <label htmlFor="otherCity" className="block text-sm font-medium text-gray-700 mb-2">
                      Specify City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="otherCity"
                      name="otherCity"
                      value={formData.address?.otherCity || ''}
                      onChange={handleAddressChange}
                      className="form-input"
                      placeholder="Please enter your city"
                      required
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="religion" className="block text-sm font-medium text-gray-700 mb-2">
                    Religion <span className="text-red-500">*</span>
                  </label>
                  
                  <SearchableSelect
                    id="religion"
                    name="religion"
                    value={formData.religion}
                    onChange={handleChange}
                    options={religions}
                    placeholder="Select Religion"
                    required
                  />
                </div>

                {/* Conditional input for Other Religion */}
                {formData.religion === 'Other' && (
                  <div>
                    <label htmlFor="otherReligion" className="block text-sm font-medium text-gray-700 mb-2">
                      Specify Other Religion <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="otherReligion"
                      name="otherReligion"
                      value={formData.otherReligion}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Please specify your religion"
                      required
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="caste" className="block text-sm font-medium text-gray-700 mb-2">
                    Caste <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    id="caste"
                    name="caste"
                    value={formData.caste}
                    onChange={handleChange}
                    options={castes}
                    placeholder="Select Caste"
                    required
                  />
                </div>

                {(formData.caste === 'SC' || formData.caste === 'ST' || formData.caste === 'OBC') && (
                  <div className="md:col-span-2">
                    <label htmlFor="casteCertificateName" className="block text-sm font-medium text-gray-700 mb-2">
                      Name of the Caste as mentioned on Caste Certificate <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FileText className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="casteCertificateName"
                        name="casteCertificateName"
                        value={formData.casteCertificateName}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter caste name as mentioned on certificate"
                        required
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="motherTongue" className="block text-sm font-medium text-gray-700 mb-2">
                    Mother Tongue <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    id="motherTongue"
                    name="motherTongue"
                    value={formData.motherTongue}
                    onChange={handleChange}
                    options={motherTongues}
                    placeholder="Select Mother Tongue"
                    required
                  />
                </div>

                {/* Conditional input for Other Mother Tongue */}
                {formData.motherTongue === 'Other' && (
                  <div>
                    <label htmlFor="otherMotherTongue" className="block text-sm font-medium text-gray-700 mb-2">
                      Specify Other Mother Tongue <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="otherMotherTongue"
                      name="otherMotherTongue"
                      value={formData.otherMotherTongue}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Please specify your mother tongue"
                      required
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="gradeApplying" className="block text-sm font-medium text-gray-700 mb-2">
                    Grade Applying For <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="gradeApplying"
                    name="gradeApplying"
                    value={formData.gradeApplying}
                    className="form-input bg-gray-100 cursor-not-allowed"
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">This field is pre-selected and cannot be changed</p>
                </div>

                <div>
                  <label htmlFor="session" className="block text-sm font-medium text-gray-700 mb-2">
                    Session <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    id="session"
                    name="session"
                    value={formData.session}
                    onChange={handleChange}
                    options={sessions}
                    placeholder="Select Session"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Family Structure Section */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="h-5 w-5 text-teal-600 mr-2" />
                Family Information
              </h2>
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Note:</span> We understand that every family is unique. Please select the option that best describes your family structure. This information helps us provide appropriate support and communication.
                </p>
              </div>
              
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <span className="font-medium">Marital Status:</span> First select whether you are married or a single parent. If you select "Single parent", you'll need to specify your specific situation (separated, divorced, etc.).
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="familyStructure" className="block text-sm font-medium text-gray-700 mb-2">
                    Family Structure <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    id="familyStructure"
                    name="familyStructure"
                    value={formData.familyStructure}
                    onChange={handleChange}
                    options={familyStructures}
                    placeholder="Select Family Structure"
                    required
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="maritalStatus" className="block text-sm font-medium text-gray-700 mb-2">
                    Marital Status <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    id="maritalStatus"
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleChange}
                    options={maritalStatuses}
                    placeholder="Select Marital Status"
                    required
                  />
                </div>

                {/* Conditional dropdown for single parent options */}
                {formData.maritalStatus === 'Single parent' && (
                  <div className="md:col-span-2">
                    <div className="border-l-4 border-teal-500 pl-4 bg-teal-50 p-3 rounded-r-lg">
                      <label htmlFor="singleParentMaritalStatus" className="block text-sm font-medium text-teal-800 mb-2">
                        Specify Single Parent Status <span className="text-red-500">*</span>
                      </label>
                      <SearchableSelect
                        id="singleParentMaritalStatus"
                        name="singleParentMaritalStatus"
                        value={formData.singleParentMaritalStatus}
                        onChange={handleChange}
                        options={singleParentOptions}
                        placeholder="Select Single Parent Status"
                        className="form-select border-teal-300 focus:border-teal-500 focus:ring-teal-500"
                        required
                      />
                      <p className="text-xs text-teal-600 mt-1">
                        Please specify your specific situation as a single parent
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Display final marital status for clarity */}
                {formData.maritalStatus && (
                  <div className="md:col-span-2">
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Selected Marital Status:</span>{' '}
                        {formData.maritalStatus === 'Single parent' && formData.singleParentMaritalStatus
                          ? `Single parent - ${formData.singleParentMaritalStatus}`
                          : formData.maritalStatus}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Father Information Section */}
            {!['Single parent - Mother only', 'Parents not present - Child living with Guardian'].includes(formData.familyStructure) && (
              <div className="border-b border-gray-200 pb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 text-teal-600 mr-2" />
                  Father's Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fatherFirstName" className="block text-sm font-medium text-gray-700 mb-2">
                      Father's First Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="fatherFirstName"
                        name="fatherFirstName"
                        value={formData.fatherFirstName}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter father's first name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="fatherMiddleName" className="block text-sm font-medium text-gray-700 mb-2">
                      Father's Middle Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="fatherMiddleName"
                        name="fatherMiddleName"
                        value={formData.fatherMiddleName}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter father's middle name (optional)"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="fatherLastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Father's Last Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="fatherLastName"
                        name="fatherLastName"
                        value={formData.fatherLastName}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter father's last name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="fatherPhone" className="block text-sm font-medium text-gray-700 mb-2">
                      Father's Contact Number <span className="text-red-500">*</span>
                    </label>
                    <PhoneInput
                      id="fatherPhone"
                      international
                      countryCallingCodeEditable={false}
                      defaultCountry="IN"
                      value={fatherPhone}
                      onChange={handleFatherPhoneChange}
                      placeholder="Enter father's contact number"
                      className="form-input"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="fatherEmail" className="block text-sm font-medium text-gray-700 mb-2">
                      Father's Email ID <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        id="fatherEmail"
                        name="fatherEmail"
                        value={formData.fatherEmail}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter father's email address"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Mother Information Section */}
            {!['Single parent - Father only', 'Parents not present - Child living with Guardian'].includes(formData.familyStructure) && (
              <div className="border-b border-gray-200 pb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 text-teal-600 mr-2" />
                  Mother's Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="motherFirstName" className="block text-sm font-medium text-gray-700 mb-2">
                      Mother's First Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="motherFirstName"
                        name="motherFirstName"
                        value={formData.motherFirstName}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter mother's first name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="motherMiddleName" className="block text-sm font-medium text-gray-700 mb-2">
                      Mother's Middle Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="motherMiddleName"
                        name="motherMiddleName"
                        value={formData.motherMiddleName}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter mother's middle name (optional)"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="motherLastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Mother's Last Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="motherLastName"
                        name="motherLastName"
                        value={formData.motherLastName}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter mother's last name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="motherPhone" className="block text-sm font-medium text-gray-700 mb-2">
                      Mother's Contact Number <span className="text-red-500">*</span>
                    </label>
                    <PhoneInput
                      id="motherPhone"
                      international
                      countryCallingCodeEditable={false}
                      defaultCountry="IN"
                      value={motherPhone}
                      onChange={handleMotherPhoneChange}
                      placeholder="Enter mother's contact number"
                      className="form-input"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="motherEmail" className="block text-sm font-medium text-gray-700 mb-2">
                      Mother's Email ID <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        id="motherEmail"
                        name="motherEmail"
                        value={formData.motherEmail}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter mother's email address"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Guardian Information Section (when parents are not present) */}
            {formData.familyStructure === 'Parents not present - Child living with Guardian' && (
              <div className="border-b border-gray-200 pb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 text-teal-600 mr-2" />
                  Guardian's Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="guardianFirstName" className="block text-sm font-medium text-gray-700 mb-2">
                      Guardian's First Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="guardianFirstName"
                        name="guardianFirstName"
                        value={formData.guardianFirstName}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter guardian's first name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="guardianMiddleName" className="block text-sm font-medium text-gray-700 mb-2">
                      Guardian's Middle Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="guardianMiddleName"
                        name="guardianMiddleName"
                        value={formData.guardianMiddleName}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter guardian's middle name (optional)"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="guardianLastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Guardian's Last Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="guardianLastName"
                        name="guardianLastName"
                        value={formData.guardianLastName}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter guardian's last name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="guardianPhone" className="block text-sm font-medium text-gray-700 mb-2">
                      Guardian's Contact Number <span className="text-red-500">*</span>
                    </label>
                    <PhoneInput
                      id="guardianPhone"
                      international
                      countryCallingCodeEditable={false}
                      defaultCountry="IN"
                      value={guardianPhone}
                      onChange={handleGuardianPhoneChange}
                      placeholder="Enter guardian's contact number"
                      className="form-input"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="guardianEmail" className="block text-sm font-medium text-gray-700 mb-2">
                      Guardian's Email ID <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        id="guardianEmail"
                        name="guardianEmail"
                        value={formData.guardianEmail}
                        onChange={handleChange}
                        className="form-input pl-12"
                        placeholder="Enter guardian's email address"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="guardianRelationship" className="block text-sm font-medium text-gray-700 mb-2">
                      Guardian's Relationship to Student <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      id="guardianRelationship"
                      name="guardianRelationship"
                      value={formData.guardianRelationship}
                      onChange={handleChange}
                      options={guardianRelationships}
                      placeholder="Select Relationship"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <div className="flex-1">
                    <p className="text-red-800 text-sm">{error}</p>
                    {/* Show retry button for network/backend issues */}
                    {(error.includes('Backend connection failed') || error.includes('Network error') || error.includes('Unable to load application data')) && (
                      <button
                        type="button"
                        onClick={() => {
                          setError('');
                          loadExistingData();
                        }}
                        className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                      >
                        Try Again
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}


            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <p className="text-green-800 text-sm">{success}</p>
                </div>
              </div>
            )}

            {/* Application Status Indicator */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full ${formData.applicationStage1Filled === 'Yes' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                  <div>
                    <h3 className="text-sm font-medium text-blue-900">
                      Application Stage 1 Status
                    </h3>
                    <p className="text-sm text-blue-700">
                      {formData.applicationStage1Filled === 'Yes' ? 'Completed' : 'Pending'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-blue-600">
                    {formData.applicationStage1Filled === 'Yes' ? 'You can view and edit your application' : 'Please fill out the form to start your application'}
                  </p>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {getApplicationButtonText()}
                  </>
                )}
              </button>

              {formData.stage1Completed && (
                <button
                  type="button"
                  onClick={async () => {
                    // Check payment status before redirecting
                    const currentPaymentStatus = await fetchPaymentStatus();
                    setPaymentStatus(currentPaymentStatus);
                    
                    if (currentPaymentStatus === 'completed') {
                      // Redirect to dashboard if payment is already completed
                      navigate('/dashboard');
                    } else {
                      // Redirect to payment if not completed
                      navigate('/payment');
                    }
                  }}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                >
                  <ArrowRight className="h-4 w-4 mr-2" />
                  {paymentStatus === 'completed' ? 'Go to Dashboard' : 'Proceed to Payment'}
                </button>
              )}
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Stage1Form;
